import React, { useEffect, useState } from 'react';
import type { TableProps } from 'antd';
import { Table } from 'antd';
import classNames from 'classnames';
import styles from './styles.module.pcss';

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

interface DataTableProps<T> extends Omit<TableProps<T>, 'loading'> {
  loading?: boolean;
  customStyles?: {
    background?: string;
    borderRadius?: string | number;
    padding?: string | number;
    maxHeight?: string | number;
    pagination?: {
      fontSize?: string | number;
      color?: string;
      activeColor?: string;
    };
  };
  showCheckboxes?: boolean;
  handleRowsSelected?: (rows: any[]) => void;
  handleRowKeysSelected?: (rowKeys: string[]) => void;
  clearSelections?: boolean;
}

const DataTable = <T extends { key: string }>({
  loading,
  className,
  customStyles,
  pagination = {
    position: ['bottomLeft'],
    showSizeChanger: true,
  },
  scroll = { y: customStyles?.maxHeight || 500 },
  showCheckboxes,
  handleRowsSelected,
  handleRowKeysSelected,
  clearSelections,
  ...restProps
}: DataTableProps<T>) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: T[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    handleRowKeysSelected?.(newSelectedRowKeys as string[]);
    handleRowsSelected?.(selectedRows);
  };

  useEffect(() => {
    if (clearSelections) {
      setSelectedRowKeys([]);
      handleRowsSelected?.([]);
      handleRowKeysSelected?.([]);
    }
  }, [clearSelections, handleRowKeysSelected, handleRowsSelected]);

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
  };

  const tableStyle = {
    '--custom-background': customStyles?.background,
    '--custom-border-radius':
      typeof customStyles?.borderRadius === 'number' ? `${customStyles.borderRadius}px` : customStyles?.borderRadius,
    '--custom-padding': typeof customStyles?.padding === 'number' ? `${customStyles.padding}px` : customStyles?.padding,
    '--custom-max-height':
      typeof customStyles?.maxHeight === 'number' ? `${customStyles.maxHeight}px` : customStyles?.maxHeight,
    '--custom-pagination-font-size':
      typeof customStyles?.pagination?.fontSize === 'number'
        ? `${customStyles.pagination.fontSize}px`
        : customStyles?.pagination?.fontSize,
    '--custom-pagination-color': customStyles?.pagination?.color,
    '--custom-pagination-active-color': customStyles?.pagination?.activeColor,
  } as React.CSSProperties;

  return (
    <Table<T>
      className={classNames(styles.dataTable, className)}
      {...restProps}
      loading={loading}
      pagination={pagination}
      scroll={scroll}
      style={tableStyle}
      rowSelection={showCheckboxes ? rowSelection : undefined}
    />
  );
};

export default DataTable;
